import {
  brandExpressionId,
  Expression as RExpression,
  isStringTemplate,
} from '@frontapp/workflows/expressions';
import {isEmpty, isNumber, isString, isUndefined} from 'lodash';
import React, {ReactNode} from 'react';
import uuid from 'uuid-random';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  DecisionTreeNodeDataParseExpressionFrontChat,
  DecisionTreeNodeDataParseExpressionOptionFrontChat,
  ParseExpressionConditionBetweenTimeValue,
  ParseExpressionConditionValue,
  ParseExpressionOperationsEnum,
  ParseExpressionResultTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataFrontChatModel';
import {getPropertyLabel} from '../../../../settings/editors/expressions/expressionLabels';
import {Expression} from '../../../../settings/editors/expressions/expressionModel';
import {
  areInferredTypesEqual,
  BooleanResult,
  DateTimeResult,
  isStringLikeInferredType,
  NumberResult,
} from '../../../../settings/editors/expressions/infer/internal';
import {makeGroupByOptions} from '../../../../settings/helpers/groupByOptionsHelpers';
import {castAsPreExpressions} from '../../flowBuilder/expressionHelpers';

/*
 * Constants.
 */

const unaryOperations = [
  ParseExpressionOperationsEnum.IS_TRUE,
  ParseExpressionOperationsEnum.IS_FALSE,
  ParseExpressionOperationsEnum.EXISTS,
  ParseExpressionOperationsEnum.NOT_EXIST,
  ParseExpressionOperationsEnum.ELSE,
];

export const defaultChatBotExpressions = Object.freeze(
  castAsPreExpressions(buildDefaultChatBotBuilderExpressions()),
);

const defaultChatBotVisitorDataExpressionIds = defaultChatBotExpressions
  .filter((expression) => expression.atoms[0].params?.property !== 'contact_fields')
  .map((expression) => expression.id);

const defaultChatBotContactFieldsExpressionIds = defaultChatBotExpressions
  .filter((expression) => expression.atoms[0].params?.property === 'contact_fields')
  .map((expression) => expression.id);

export const defaultChatBotExpressionPlaceholders: Record<string, string> = {
  'default:1': 'e.g. Chrome, Firefox', // Browser name
  'default:2': 'e.g. en-US, fr-FR', // Browser language
  'default:3': 'e.g. 1.0.0', // Browser version
  // 'default:4': 'United States', // Country
  'default:5': 'e.g. 12', // Current hour
  'default:6': 'e.g. 128.0.0.1', // IP address
  'default:7': 'e.g. false', // Is mobile
  'default:8': 'e.g. Macintosh, Windows', // OS
  'default:9': 'e.g. https://example.com', // Page URL
  'default:10': 'e.g. contact@email.com', // Contact email
  'default:11': 'e.g. John Doe', // Contact name
  'default:12': 'e.g. +1234567890', // Contact phone
  'default:13': 'e.g. @twitter_handle', // Contact Twitter
};

export const defaultParseExpressionConditionBetweenTimeValue: ParseExpressionConditionBetweenTimeValue = {
  left: undefined,
  right: undefined,
};

/*
 * Helpers.
 */

export function buildDefaultChatBotBuilderExpressions(): ReadonlyArray<RExpression> {
  return [
    // Visitor data.
    {
      id: brandExpressionId('default:1'),
      name: getPropertyLabel('name_chatBotBrowser'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'browser'},
        },
        {
          transform: 'get',
          params: {property: 'name'},
        },
      ],
    },
    {
      id: brandExpressionId('default:2'),
      name: getPropertyLabel('language_chatBotBrowser'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'browser'},
        },
        {
          transform: 'get',
          params: {property: 'language'},
        },
      ],
    },
    {
      id: brandExpressionId('default:3'),
      name: getPropertyLabel('version_chatBotBrowser'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'browser'},
        },
        {
          transform: 'get',
          params: {property: 'version'},
        },
      ],
    },
    // TODO: Reintroduce `country` once the BE is able to evaluate it.
    // {
    //   id: brandExpressionId('default:4'),
    //   name: getPropertyLabel('country'),
    //   atoms: [
    //     {
    //       transform: 'get',
    //       params: {property: 'country'},
    //     },
    //   ],
    // },
    // TODO (cmckee): Re-enable this as a default variable when the UI supports a time input.
    // {
    //   id: brandExpressionId('default:5'),
    //   name: getPropertyLabel('current_time'),
    //   atoms: [
    //     {
    //       transform: 'get',
    //       params: {property: 'current_time'},
    //     },
    //   ],
    // },
    {
      id: brandExpressionId('default:5'),
      name: getPropertyLabel('current_hour'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'date'},
        },
        {
          transform: 'get_hours',
          params: {timezone: 'America/Los_Angeles'},
        },
      ],
    },
    {
      id: brandExpressionId('default:6'),
      name: getPropertyLabel('ip_address'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'browser'},
        },
        {
          transform: 'get',
          params: {property: 'ip_address'},
        },
      ],
    },
    {
      id: brandExpressionId('default:7'),
      name: getPropertyLabel('is_mobile'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'is_mobile'},
        },
      ],
    },
    {
      id: brandExpressionId('default:8'),
      name: getPropertyLabel('os'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'os'},
        },
      ],
    },
    {
      id: brandExpressionId('default:9'),
      name: getPropertyLabel('page_url'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'page_url'},
        },
      ],
    },
    // Contact fields.
    {
      id: brandExpressionId('default:10'),
      name: getPropertyLabel('contact_email'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'contact_fields'},
        },
        {
          transform: 'get',
          params: {property: 'email'},
        },
      ],
    },
    {
      id: brandExpressionId('default:11'),
      name: getPropertyLabel('contact_name'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'contact_fields'},
        },
        {
          transform: 'get',
          params: {property: 'name'},
        },
      ],
    },
    {
      id: brandExpressionId('default:12'),
      name: getPropertyLabel('contact_phone'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'contact_fields'},
        },
        {
          transform: 'get',
          params: {property: 'phone'},
        },
      ],
    },
    {
      id: brandExpressionId('default:13'),
      name: getPropertyLabel('contact_twitter'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'contact_fields'},
        },
        {
          transform: 'get',
          params: {property: 'twitter'},
        },
      ],
    },
  ];
}

function checkIsChatBotVisitorDataExpression(expression: RExpression) {
  return defaultChatBotVisitorDataExpressionIds.includes(expression.id);
}

function checkIsChatBotContactExpression(expression: RExpression) {
  return defaultChatBotContactFieldsExpressionIds.includes(expression.id);
}

/**
 * For a given result type (the value that will come out of an expression), compute the default options that are
 * available in the parse expression node.
 */
export function buildDefaultParseExpressionOptions(
  resultType: ParseExpressionResultTypesEnum,
): ReadonlyArray<DecisionTreeNodeDataParseExpressionOptionFrontChat> {
  return [
    {
      operation: computeDefaultParseExpressionOperation(resultType),
      value: computeDefaultParseExpressionOptionValue(resultType),
      sourceHandle: uuid(),
    },
    {
      operation: ParseExpressionOperationsEnum.ELSE,
      value: undefined,
      sourceHandle: uuid(),
    },
  ];
}

/**
 * For a given result type and an existing parse expression node, compute the default options and then copy the existing sourceHandles to the new options.
 * This is used when we want to change the expression type and keep the existing branches from each option, because the list of operators will change for each type.
 * If no existing data is provided, the default options will be created.
 */
export function computeParseExpressionOptions(
  resultType: ParseExpressionResultTypesEnum,
  existingData?: DecisionTreeNodeDataParseExpressionFrontChat,
): ReadonlyArray<DecisionTreeNodeDataParseExpressionOptionFrontChat> {
  if (!existingData) {
    return buildDefaultParseExpressionOptions(resultType);
  }

  const availableOperations = computeAvailableParseExpressionOperations(resultType);
  const existingOptions = existingData.options;

  // If we are switching to an expression with the same resultType, we can just return the existing options.
  if (existingData.resultType === resultType) {
    return existingOptions;
  }

  // The last option is always the else operation.
  const elseOption = existingOptions[existingOptions.length - 1];

  // Only copy over the minimum between the existing options and the available operations, minus the else option.
  const optionCount = Math.min(existingOptions.length - 1, availableOperations.length);

  // Create default options for the existing sourceHandles.
  const operatorOptions = existingOptions.slice(0, optionCount).map(({sourceHandle}, index) => {
    const operation = availableOperations[index];

    return {
      operation,
      value: computeDefaultParseExpressionOptionValue(resultType),
      sourceHandle,
    };
  });

  return [...operatorOptions, elseOption];
}

/** For the given expression result type, determine the operation provided in the default parse expression option. */
function computeDefaultParseExpressionOperation(resultType: ParseExpressionResultTypesEnum) {
  const availableOperations = computeAvailableParseExpressionOperations(resultType);

  if (resultType === ParseExpressionResultTypesEnum.BOOLEAN) {
    return ParseExpressionOperationsEnum.IS_TRUE;
  }

  return availableOperations[0];
}

/** For the given expression result type, determine the default value in the parse expression option.  */
function computeDefaultParseExpressionOptionValue(resultType: ParseExpressionResultTypesEnum) {
  switch (resultType) {
    case ParseExpressionResultTypesEnum.DATE_TIME:
    case ParseExpressionResultTypesEnum.STRING:
      return '';
    case ParseExpressionResultTypesEnum.NUMBER:
    case ParseExpressionResultTypesEnum.BOOLEAN:
    default:
      return undefined;
  }
}

export function computeParseExpressionResultType(expression: Expression): ParseExpressionResultTypesEnum {
  const {inferredType} = expression;

  if (areInferredTypesEqual(NumberResult, inferredType)) {
    return ParseExpressionResultTypesEnum.NUMBER;
  }

  if (areInferredTypesEqual(DateTimeResult, inferredType)) {
    return ParseExpressionResultTypesEnum.DATE_TIME;
  }

  if (areInferredTypesEqual(BooleanResult, inferredType)) {
    return ParseExpressionResultTypesEnum.BOOLEAN;
  }

  if (isStringLikeInferredType(inferredType)) {
    return ParseExpressionResultTypesEnum.STRING;
  }

  return ParseExpressionResultTypesEnum.OTHER;
}

export function computeAvailableParseExpressionOperations(
  parseExpressionResultType: ParseExpressionResultTypesEnum,
  hasChatBotDynamicVariableBetweenTimeInput: boolean = false,
): ReadonlyArray<ParseExpressionOperationsEnum> {
  switch (parseExpressionResultType) {
    case ParseExpressionResultTypesEnum.NUMBER:
      if (hasChatBotDynamicVariableBetweenTimeInput) {
        return [
          ParseExpressionOperationsEnum.EQUALS,
          ParseExpressionOperationsEnum.NOT_EQUAL,
          ParseExpressionOperationsEnum.GREATER_THAN,
          ParseExpressionOperationsEnum.LOWER_THAN,
          ParseExpressionOperationsEnum.BETWEEN_TIME,
          ParseExpressionOperationsEnum.EXISTS,
          ParseExpressionOperationsEnum.NOT_EXIST,
        ];
      }
      return [
        ParseExpressionOperationsEnum.EQUALS,
        ParseExpressionOperationsEnum.NOT_EQUAL,
        ParseExpressionOperationsEnum.GREATER_THAN,
        ParseExpressionOperationsEnum.LOWER_THAN,
        ParseExpressionOperationsEnum.EXISTS,
        ParseExpressionOperationsEnum.NOT_EXIST,
      ];

    case ParseExpressionResultTypesEnum.BOOLEAN:
      return [ParseExpressionOperationsEnum.IS_TRUE, ParseExpressionOperationsEnum.IS_FALSE];

    case ParseExpressionResultTypesEnum.STRING:
      return [
        ParseExpressionOperationsEnum.CONTAINS,
        ParseExpressionOperationsEnum.NOT_CONTAIN,
        ParseExpressionOperationsEnum.EQUALS,
        ParseExpressionOperationsEnum.NOT_EQUAL,
        ParseExpressionOperationsEnum.STARTS_WITH,
        ParseExpressionOperationsEnum.ENDS_WITH,
        ParseExpressionOperationsEnum.EXISTS,
        ParseExpressionOperationsEnum.NOT_EXIST,
      ];

    case ParseExpressionResultTypesEnum.DATE_TIME:
      return [
        // TODO: Re-enable these when the UI supports a time input.
        // ParseExpressionOperationsEnum.BEFORE,
        // ParseExpressionOperationsEnum.AFTER,
        ParseExpressionOperationsEnum.EXISTS,
        ParseExpressionOperationsEnum.NOT_EXIST,
      ];

    case ParseExpressionResultTypesEnum.OTHER:
    default:
      return [ParseExpressionOperationsEnum.EXISTS, ParseExpressionOperationsEnum.NOT_EXIST];
  }
}

export function isParseExpressionUnaryOperation(operation: ParseExpressionOperationsEnum) {
  return unaryOperations.includes(operation);
}

export function isParseExpressionOptionEmpty(option: DecisionTreeNodeDataParseExpressionOptionFrontChat) {
  const {operation, value} = option;

  // If we have a BetweenTimeValue, we need to check if either the left or right value is empty.
  if (isParseExpressionConditionBetweenTimeValue(value)) {
    // If a user enters values into the between_time input and then changes the operation, the value will still have the form {left, right} until the user interacts with the new input.
    // We should treat this as empty and not allow saving.
    if (operation !== ParseExpressionOperationsEnum.BETWEEN_TIME) {
      return true;
    }

    return isUndefined(value.left) || isUndefined(value.right);
  }

  // If our operation is BETWEEN_TIME, but we don't have the correct value type, we should treat this as empty and not allow saving.
  if (operation === ParseExpressionOperationsEnum.BETWEEN_TIME) {
    return true;
  }

  const isValueEmpty =
    isUndefined(value) || (isString(value) && !value) || (isStringTemplate(value) && isEmpty(value.template));

  return isValueEmpty && !isParseExpressionUnaryOperation(operation);
}

/** Show an error if the user has entered a left and right value for a BETWEEN_TIME operation, but the right value is not greater than the left. */
export function isParseExpressionBetweenOptionValid(
  option: DecisionTreeNodeDataParseExpressionOptionFrontChat,
) {
  const {value} = option;

  if (!isParseExpressionConditionBetweenTimeValue(value)) {
    return false;
  }

  return isUndefined(value.left) || isUndefined(value.right) || value.left < value.right;
}

/*
 * Renderers.
 */

/** Translated labels for every kind of parse expression operation. */
export const operationLabels: Record<ParseExpressionOperationsEnum, ReactNode> = {
  [ParseExpressionOperationsEnum.EQUALS]: <Trans id="parse-expression-operation-equals">Equals</Trans>,
  [ParseExpressionOperationsEnum.NOT_EQUAL]: (
    <Trans id="parse-expression-operation-not-equal">Does not equal</Trans>
  ),
  [ParseExpressionOperationsEnum.STARTS_WITH]: (
    <Trans id="parse-expression-operation-starts-with">Starts with</Trans>
  ),
  [ParseExpressionOperationsEnum.ENDS_WITH]: (
    <Trans id="parse-expression-operation-ends-with">Ends with</Trans>
  ),
  [ParseExpressionOperationsEnum.CONTAINS]: <Trans id="parse-expression-operation-contains">Contains</Trans>,
  [ParseExpressionOperationsEnum.NOT_CONTAIN]: (
    <Trans id="parse-expression-operation-not-contain">Does not contain</Trans>
  ),
  [ParseExpressionOperationsEnum.GREATER_THAN]: (
    <Trans id="parse-expression-operation-greater-than">Is greater than</Trans>
  ),
  [ParseExpressionOperationsEnum.LOWER_THAN]: (
    <Trans id="parse-expression-operation-lower-than">Is lower than</Trans>
  ),
  [ParseExpressionOperationsEnum.BETWEEN_TIME]: (
    <Trans id="parse-expression-operation-between-time">Is between</Trans>
  ),
  [ParseExpressionOperationsEnum.BEFORE]: <Trans id="parse-expression-operation-before">Is before</Trans>,
  [ParseExpressionOperationsEnum.AFTER]: <Trans id="parse-expression-operation-after">Is after</Trans>,
  [ParseExpressionOperationsEnum.IS_TRUE]: <Trans id="parse-expression-operation-is-true">Is true</Trans>,
  [ParseExpressionOperationsEnum.IS_FALSE]: <Trans id="parse-expression-operation-is-false">Is false</Trans>,
  [ParseExpressionOperationsEnum.EXISTS]: <Trans id="parse-expression-operation-exists">Exists</Trans>,
  [ParseExpressionOperationsEnum.NOT_EXIST]: (
    <Trans id="parse-expression-operation-not-exist">Does not exist</Trans>
  ),
  [ParseExpressionOperationsEnum.ELSE]: <Trans id="parse-expression-operation-else">Else</Trans>,
};

enum ChatbotExpressionGroupByGroupsEnum {
  VISITOR_DATA = 'visitor_data',
  CONTACT_FIELDS = 'contact_fields',
  CUSTOM = 'custom',
}

export const chatbotExpressionGroupByOptions = makeGroupByOptions({
  iteratee: (expression) => {
    if (checkIsChatBotVisitorDataExpression(expression)) {
      return ChatbotExpressionGroupByGroupsEnum.VISITOR_DATA;
    }

    if (checkIsChatBotContactExpression(expression)) {
      return ChatbotExpressionGroupByGroupsEnum.CONTACT_FIELDS;
    }

    return ChatbotExpressionGroupByGroupsEnum.CUSTOM;
  },
  renderGroupHeader: (group) => {
    switch (group) {
      case ChatbotExpressionGroupByGroupsEnum.VISITOR_DATA:
        return <Trans id="chat-bot-builder-string-template-parameter-group-visitor-data">Visitor data</Trans>;
      case ChatbotExpressionGroupByGroupsEnum.CONTACT_FIELDS:
        return (
          <Trans id="chat-bot-builder-string-template-parameter-group-contact-fields">
            Default contact fields
          </Trans>
        );
      case ChatbotExpressionGroupByGroupsEnum.CUSTOM:
        return (
          <Trans id="chat-bot-builder-string-template-parameter-group-custom-variables">
            Custom variables
          </Trans>
        );
      default:
        return null;
    }
  },
});

/**
 * If the value parameter is a ParseExpressionConditionBetweenTimeValue, return it.
 * Otherwise, return the default empty ParseExpressionConditionBetweenTimeValue.
 */
export function computeParseExpressionConditionBetweenTimeValue(
  value: ParseExpressionConditionValue,
): ParseExpressionConditionBetweenTimeValue {
  if (!isParseExpressionConditionBetweenTimeValue(value)) {
    return defaultParseExpressionConditionBetweenTimeValue;
  }

  return value;
}

export function isParseExpressionConditionBetweenTimeValue(
  value: ParseExpressionConditionValue,
): value is ParseExpressionConditionBetweenTimeValue {
  if (!value || isString(value) || isStringTemplate(value) || isNumber(value)) {
    return false;
  }

  // Undefined values for left and right are valid in the client because the user must interact with each input one at a time.
  // When sending this information to the server, we require both values to be numbers.
  return (
    (isUndefined(value.left) || isNumber(value.left)) && (isUndefined(value.right) || isNumber(value.right))
  );
}
